import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import pickBy from 'lodash/pickBy';
import type { BlockEditProps, BlocksConfigData } from '@plone/types';
import { BlocksForm } from '@plone/volto/components/manage/Form';
import EditBlockWrapper from '@plone/volto/components/manage/Blocks/Container/EditBlockWrapper';
import { SidebarPortal, Icon } from '@plone/volto/components';
// @ts-ignore-error
import { BlockDataForm } from '@plone/volto/components/manage/Form';
import { setUIState } from '@plone/volto/actions/form/form';
import { blocksFormGenerator } from '@plone/volto/helpers/Blocks/Blocks';
import type { ExpandableGridData } from './schema';
import Toolbar from './Toolbar';
import blockIcon from '@plone/volto/icons/grid-block.svg';
import styles from './styles.module.css';

type Props = BlockEditProps & {
  data: ExpandableGridData;
};

const ExpandableGridBlockEdit = (props: Props) => {
  const {
    block,
    data,
    onChangeBlock,
    onChangeField,
    selected,
    manage,
    pathname,
    blocksErrors,
    // ...otherProps
  } = props;
  const intl = useIntl();
  const metadata = props.metadata || props.properties;
  const blockConfig = props.blocksConfig[data['@type']];
  const blocksConfig = blockConfig.blocksConfig || props.blocksConfig;
  const allowedBlocks = blockConfig.allowedBlocks;
  const defaultBlock = allowedBlocks.length === 1 ? allowedBlocks[0] : '';
  const columnsCount = data.columns?.length ?? 0;

  useEffect(() => {
    if (columnsCount === 0) {
      onChangeBlock(block, {
        ...data,
        columns: [
          blocksFormGenerator(1, defaultBlock),
          blocksFormGenerator(1, defaultBlock),
          blocksFormGenerator(1, defaultBlock),
        ],
      });
    }
  }, [columnsCount, defaultBlock, data, onChangeBlock, props.block]);

  // @ts-expect-error
  const selectedBlock = useSelector((state) => state.form.ui.gridSelected);
  const dispatch = useDispatch();

  function onChangeBlockColumn(columnIndex: number, value: any) {
    const newColumns = [...data.columns];
    newColumns[columnIndex].blocks = value.blocks;
    newColumns[columnIndex].blocks_layout = value.blocks_layout;
    onChangeBlock(block, {
      ...data,
      columns: newColumns,
    });
  }

  function setSelectedBlock(id: string) {
    dispatch(setUIState({ gridSelected: id }));
  }

  function onAddNewBlock(column: number) {
    const newColumn = { ...data.columns[column] };
    const newBlock = blocksFormGenerator(1, defaultBlock);
    newColumn.blocks_layout.items = newColumn.blocks_layout.items.concat(
      newBlock.blocks_layout.items,
    );
    newColumn.blocks = {
      ...newColumn.blocks,
      ...newBlock.blocks,
    };
    onChangeBlockColumn(column, newColumn);
  }

  const blockState = {};

  const restrictedBlocksConfig = {
    ...blocksConfig,
    //remove styling fields from cardwithimage
    cardwithimages: {
      ...blocksConfig.cardwithimages,
      blockSchema: ({ data, intl }) => {
        const schema = blocksConfig[data['@type']].blockSchema({
          data,
          intl,
        });

        const stylingFields =
          schema.properties.styles.schema.fieldsets[0].fields;

        schema.properties.styles.schema.fieldsets[0].fields =
          stylingFields.filter(
            (field) =>
              ![
                'align',
                'bg',
                'cardBg',
                'fullwidth',
                'add_top_line',
                'add_bottom_line',
                'line_color',
              ].includes(field),
          );

        return schema;
      },
    },
  };

  const allowedBlocksConfig = pickBy(
    restrictedBlocksConfig,
    (value, key: keyof BlocksConfigData) => allowedBlocks.includes(key),
  );

  const schema = blocksConfig[data['@type']].blockSchema({
    data,
    intl,
  });

  return (
    <div
      className={styles.grid}
      // This is required to enabling a small "in-between" clickable area
      // for bringing the Grid sidebar alive once you have selected an inner block
      onClick={(e) => {
        // @ts-expect-error
        if (!e.block) dispatch(setUIState({ gridSelected: null }));
      }}
      role="presentation"
    >
      {data.columns &&
        data.columns.map((column, index) => {
          return (
            <div className={styles.column} key={index}>
              {selected && (
                <Toolbar
                  data={column}
                  onAddNewBlock={() => onAddNewBlock(index)}
                />
              )}
              <BlocksForm
                metadata={metadata}
                properties={column}
                direction="vertical"
                manage={manage}
                selectedBlock={selected ? selectedBlock : null}
                blocksConfig={allowedBlocksConfig}
                title={data.placeholder}
                isMainForm={false}
                stopPropagation={selectedBlock}
                onSelectBlock={(id: string) => {
                  setSelectedBlock(id);
                }}
                onChangeFormData={(newFormData) => {
                  onChangeBlockColumn(index, {
                    ...column,
                    ...newFormData,
                  });
                }}
                onChangeField={(id: string, value: any) => {
                  if (['blocks', 'blocks_layout'].includes(id)) {
                    blockState[id] = value;
                    onChangeBlockColumn(index, {
                      ...column,
                      ...blockState,
                    });
                  } else {
                    onChangeField(id, value);
                  }
                }}
                pathname={pathname}
              >
                {({ draginfo }, editBlock, blockProps) => (
                  <EditBlockWrapper
                    draginfo={draginfo}
                    blockProps={{
                      ...blockProps,
                    }}
                    allowedBlocks={allowedBlocks}
                    multipleBlocksColumn={column.blocks_layout.items.length > 1}
                  >
                    {editBlock}
                  </EditBlockWrapper>
                )}
              </BlocksForm>
            </div>
          );
        })}
      {/* @ts-expect-error */}
      <SidebarPortal selected={selected && !selectedBlock}>
        {schema && (
          <BlockDataForm
            icon={<Icon size="24px" name={blockIcon} />}
            schema={schema}
            title={schema.title}
            onChangeField={(id: string, value: unknown) => {
              onChangeBlock(block, {
                ...data,
                [id]: value,
              });
            }}
            onChangeBlock={onChangeBlock}
            formData={data}
            block={block}
            errors={blocksErrors}
          />
        )}
      </SidebarPortal>
    </div>
  );
};

export default ExpandableGridBlockEdit;
